<template>
  <label
    :class="[
      {
        error: hasError,
        disabled: disabled,
        required: isRequired,
      },
      cssClass || '',
    ]"
  >
    <span class="label-wrapper">
      <slot name="label" />
      <slot name="label-status">
        <span v-if="isRequired" class="label-status">*</span>
        <translate v-else class="label-help">(optional)</translate>
      </slot>
      <template v-if="validationRuleMax">
        <span class="textarea-counter" :class="{ 'over-maximum': isOverMax }"
          >{{ charCount }} / {{ validationRuleMax }}</span
        >
      </template>
    </span>
    <textarea
      v-bind="{ ...$attrs }"
      ref="baseTextarea"
      :name="name"
      :rows="rows"
      :required="isRequired"
      :value="textareaValue"
      :class="{ autogrow: autogrow !== false }"
      @input="handleInput"
      @blur="handleBlur"
    />
  </label>
  <transition name="fade">
    <span v-if="hasError" class="input-error-msg">
      <slot
        v-if="errors.length"
        name="errorMessage"
        :failed-rules="errors"
        :has-error="getValidationError"
      />
      <translate v-else-if="parentErrors.length">
        {{ parentErrors[0] }}
      </translate>
    </span>
  </transition>
  <p v-if="$slots.helpText" class="help-text">
    <slot name="helpText" />
  </p>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useField } from 'vee-validate'
import { ZodType } from 'zod'
import { onUnmounted, ref } from 'vue'

let focusTimeoutId: ReturnType<typeof setTimeout> | null = null

const props = withDefaults(
  defineProps<{
    name: string
    validationRules?: ZodType
    cssClass?: string
    disabled?: boolean
    required?: boolean | string
    rows?: string | number
    validationErrors?: string[]
    autogrow?: boolean
    maxRows?: number
    shouldFocus?: boolean
  }>(),
  {
    validationRules: undefined,
    cssClass: '',
    disabled: false,
    required: false,
    rows: undefined,
    validationErrors: () => [],
    autogrow: true,
    maxRows: 16,
    shouldFocus: false,
  },
)

const model = defineModel<string>()

const emit = defineEmits(['input', 'blur', 'validated'])

const validationRules = computed(() =>
  props.validationRules ? toTypedSchema(props.validationRules) : undefined,
)

const {
  value: textareaValue,
  validate,
  errors,
  meta,
} = useField(props.name, validationRules.value, {
  initialValue: model.value,
})

const parentErrors = ref<string[]>([])
watch(
  () => props.validationErrors,
  (newVal) => {
    parentErrors.value = newVal
  },
)

onMounted(async () => {
  adjustHeight()

  // Focus the textarea if shouldFocus prop is true
  await nextTick(() => {
    if (props.shouldFocus && baseTextarea.value) {
      baseTextarea.value?.scrollIntoView({
        behavior: 'instant',
        block: 'start',
        inline: 'nearest',
      })

      focusTimeoutId = setTimeout(() => {
        baseTextarea.value?.focus()
      }, 500)
    }
  })
})

onUnmounted(() => {
  if (focusTimeoutId) {
    clearTimeout(focusTimeoutId)
    focusTimeoutId = null
  }
})

watch(model, async (newValue) => {
  textareaValue.value = newValue
  await validate()
  adjustHeight()
})

const validationRuleMax = computed(() => {
  return (
    props.validationRules?.maxLength || useAttrs()['max-length'] || undefined
  )
})

const isOverMax = computed(() => {
  return model.value ? model.value.length > validationRuleMax.value : false
})

const charCount = computed(() => {
  if (!model.value) {
    return '0'
  } else if (model.value.length > validationRuleMax.value) {
    return validationRuleMax.value - model.value.length
  }
  return model.value.length
})

async function handleInput(event: Event) {
  const value = (event.target as HTMLTextAreaElement).value
  model.value = value
  textareaValue.value = value
  parentErrors.value = []
  if (meta.touched) {
    await validate()
  }
}

async function handleBlur() {
  const valid = await validate()
  if (valid.valid) {
    emit('validated', valid.valid)
  }
  emit('blur')
}

const hasError = computed(
  () => errors.value.length || parentErrors.value.length,
)

function getValidationError(error: string) {
  if (errors.value.length) {
    return errors.value.includes(error)
  }
  return false
}
const isRequired = computed(() => {
  if (typeof props.required === 'string') {
    return props.required.toLowerCase() === 'true'
  }
  return props.required || false
})

const baseTextarea = ref<HTMLTextAreaElement>()

defineExpose({
  baseTextarea,
})

function adjustHeight() {
  if (!props.autogrow || !baseTextarea.value) {
    return
  }

  baseTextarea.value.style.height = 'auto'
  const newHeight = baseTextarea.value.scrollHeight
  const maxHeight =
    props.maxRows * parseInt(getComputedStyle(baseTextarea.value).lineHeight)
  baseTextarea.value.style.height = Math.min(newHeight, maxHeight) + 'px'

  if (newHeight > maxHeight) {
    baseTextarea.value.classList.add('scrollbar-visible')
  } else {
    baseTextarea.value.classList.remove('scrollbar-visible')
  }
}
</script>
<style scoped>
label {
  position: relative;
  display: inline-block;
  margin-top: 1em;
  vertical-align: text-top;
}

textarea {
  display: block;
  line-height: 1.1;
  scroll-margin-top: 4em;
}

textarea.autogrow {
  overflow-y: hidden;
}

textarea.scrollbar-visible {
  overflow-y: scroll;
}

.asblock {
  display: block;
}

.label-wrapper {
  display: flex;
  align-items: center;
}

.textarea-counter {
  font-size: small;
  color: var(--colour-text-light);
  font-weight: normal;
  margin-left: auto;
}

.label-help {
  margin-left: 0.25em;
  color: var(--colour-text-light);
}

.over-maximum {
  color: #cc0000;
}
</style>
